/**
 * ReleaseTimelineContainer Component
 * 
 * Main container component that manages 4 swimlanes and handles responsive layout,
 * date scaling, and overall timeline coordination.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Box, Typography, Paper, useTheme, Divider } from '@mui/material'
import { CategorizedRelease, ReleaseCategory } from '../../types/release'
import { DEFAULT_SWIMLANE_CONFIGS } from '../../services/releaseCategorizationService'
import {
  calculateSwimlanePositions,
  getResponsiveLayoutConfig,
  groupReleasesBySwimlane,
  calculateTotalContainerHeight,
  DEFAULT_LAYOUT_CONFIG,
  SwimlanePosition
} from './swimlaneLayout'
import { getTimelineeDateRange } from '../../utils/releaseDateFiltering'
import { generateThemeAwareStyles } from '../../styles/releaseTimelineStyles'
import SwimLane from './SwimLane'
import TimelineScale from './TimelineScale'

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface ReleaseTimelineContainerProps {
  /** Categorized releases to display */
  releases: CategorizedRelease[]

  /** Date range for timeline (auto-calculated if not provided) */
  dateRange?: { start: Date; end: Date }

  /** Initial viewport range for positioning the initial scroll view */
  initialViewport?: { start: Date; end: Date }

  /** Whether to show swimlane labels */
  showLabels?: boolean

  /** Whether to enable interactions */
  enableInteractions?: boolean
  
  /** Callback when a release is clicked */
  onReleaseClick?: (release: CategorizedRelease) => void
  
  /** Callback when a swimlane is hovered */
  onSwimlaneHover?: (category: ReleaseCategory, isHovered: boolean) => void
  
  /** Loading state */
  isLoading?: boolean
  
  /** Error state */
  error?: string | null
  
  /** Custom styling overrides */
  sx?: React.CSSProperties
}

// ============================================================================
// TIMELINE CONTAINER COMPONENT
// ============================================================================

const ReleaseTimelineContainer: React.FC<ReleaseTimelineContainerProps> = ({
  releases,
  dateRange: providedDateRange,
  initialViewport,
  showLabels = true,
  enableInteractions = true,
  onReleaseClick,
  onSwimlaneHover,
  isLoading = false,
  error = null,
  sx = {}
}) => {
  const theme = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(800) // Default width
  const [activeSwimlane, setActiveSwimlane] = useState<ReleaseCategory | null>(null)
  
  // ============================================================================
  // RESPONSIVE LAYOUT HANDLING
  // ============================================================================
  
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        // Force container to use full viewport width
        const viewportWidth = window.innerWidth
        const targetWidth = Math.floor(viewportWidth * 0.95) // Use 95% of viewport

        console.log('📏 Container Width Measurement:', {
          measuredWidth: containerRef.current.offsetWidth,
          viewportWidth,
          targetWidth,
          parentElement: containerRef.current.parentElement?.tagName,
          parentWidth: containerRef.current.parentElement?.offsetWidth,
          containerStyles: window.getComputedStyle(containerRef.current).width
        })

        // Use the target width instead of measured width
        setContainerWidth(targetWidth)
      }
    }

    // Initial measurement
    updateContainerWidth()

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateContainerWidth)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])



  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  // Get responsive layout configuration
  const layoutConfig = useMemo(() => 
    getResponsiveLayoutConfig(containerWidth, DEFAULT_LAYOUT_CONFIG),
    [containerWidth]
  )
  
  // Calculate swimlane positions
  const swimlanePositions = useMemo(() => 
    calculateSwimlanePositions(layoutConfig, containerWidth, DEFAULT_SWIMLANE_CONFIGS),
    [layoutConfig, containerWidth]
  )
  
  // Group releases by swimlane
  const groupedReleases = useMemo(() => {
    const k8sReleases = releases.filter(r => r.name.toLowerCase().includes('k8s'))
    console.log('DEBUG: Timeline k8s release details:')
    k8sReleases.forEach(r => {
      console.log(`  - ${r.name}: category=${r.category}, isCategorized=${r.isCategorized}`)
    })

    const grouped = groupReleasesBySwimlane(releases)

    // Check all swimlanes for k8s releases
    Object.entries(grouped).forEach(([swimlane, swimlaneReleases]) => {
      const k8sInSwimlane = swimlaneReleases.filter(r => r.name.toLowerCase().includes('k8s'))
      if (k8sInSwimlane.length > 0) {
        console.log(`DEBUG: ${swimlane} swimlane k8s releases:`, k8sInSwimlane.map(r => r.name))
      }
    })

    return grouped
  }, [releases])
  
  // Calculate date range for timeline
  const dateRange = useMemo(() => {
    if (providedDateRange) return providedDateRange
    return getTimelineeDateRange(releases)
  }, [providedDateRange, releases])
  
  // Calculate dynamic container height based on track allocation
  const dynamicContainerHeight = useMemo(() =>
    calculateTotalContainerHeight(layoutConfig, DEFAULT_SWIMLANE_CONFIGS),
    [layoutConfig]
  )

  // Generate theme-aware styles
  const themeStyles = useMemo(() =>
    generateThemeAwareStyles(theme, theme.palette.mode === 'dark'),
    [theme]
  )

  // Timeline content area (excluding labels)
  const timelineContentLeft = 120 // Space for swimlane labels
  const viewportWidth = containerWidth - timelineContentLeft - 32 // Account for padding

  // Calculate adaptive timeline resolution and viewport constraint width
  const { viewportConstraintWidth, adaptivePixelsPerDay } = useMemo(() => {
    if (!initialViewport) return { viewportConstraintWidth: null, adaptivePixelsPerDay: 20 }

    const viewportDays = Math.ceil(
      (initialViewport.end.getTime() - initialViewport.start.getTime()) / (1000 * 60 * 60 * 24)
    )

    // Calculate available space for timeline content
    const scrollbarWidth = 20 // Reserve space for scrollbar
    const availableContentWidth = containerWidth - timelineContentLeft - scrollbarWidth

    // Calculate adaptive pixels per day to maximize space utilization
    // Aim to use 90-95% of available width for better space utilization
    const targetUtilization = 0.95 // Use 95% of available width for maximum space utilization
    const targetContentWidth = availableContentWidth * targetUtilization
    const adaptivePixelsPerDay = Math.max(20, Math.floor(targetContentWidth / viewportDays))

    // Calculate final viewport width - use the calculated width to maximize space utilization
    const viewportContentWidth = viewportDays * adaptivePixelsPerDay
    const totalViewportWidth = timelineContentLeft + viewportContentWidth + scrollbarWidth

    // Debug logging to understand what's happening
    console.log('🔍 Adaptive Resolution Debug:', {
      containerWidth,
      timelineContentLeft,
      scrollbarWidth,
      availableContentWidth,
      targetUtilization,
      targetContentWidth,
      viewportDays,
      adaptivePixelsPerDay,
      viewportContentWidth,
      totalViewportWidth,
      initialViewport: initialViewport ? {
        start: initialViewport.start.toISOString(),
        end: initialViewport.end.toISOString()
      } : null
    })

    return {
      viewportConstraintWidth: totalViewportWidth, // Use full calculated width for better space utilization
      adaptivePixelsPerDay
    }
  }, [initialViewport, timelineContentLeft, containerWidth])

  // Container styles with horizontal scrolling - force full viewport width
  const containerStyles = useMemo(() => ({
    position: 'relative' as const,
    height: dynamicContainerHeight + 60, // Add space for timeline scale
    width: '100%', // Use full width of parent (which should be 100vw)
    minWidth: '100vw', // Force minimum viewport width
    overflowX: 'auto' as const,
    overflowY: 'hidden' as const,
    // Standard border radius for clean separation
    borderRadius: '8px',
    marginTop: 0,
    // Smooth scrolling for better UX
    scrollBehavior: 'smooth' as const,
    // Custom scrollbar styling
    '&::-webkit-scrollbar': {
      height: 8
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      borderRadius: 4
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
      borderRadius: 4,
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
      }
    },
    ...themeStyles.container,
    ...sx
  }), [dynamicContainerHeight, themeStyles.container, sx, theme.palette.mode, viewportConstraintWidth])

  // Calculate actual timeline width based on date range and adaptive resolution
  const timelineContentWidth = useMemo(() => {
    const totalDays = Math.ceil(
      (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
    )

    // Use adaptive pixels per day for consistent resolution across viewport and extended timeline
    const calculatedWidth = totalDays * adaptivePixelsPerDay

    // Ensure timeline is always at least 3x the viewport width to force scrolling
    const minimumTimelineWidth = viewportWidth * 3
    return Math.max(calculatedWidth, minimumTimelineWidth)
  }, [dateRange, viewportWidth, adaptivePixelsPerDay])

  // Scrollable content styles
  const scrollableContentStyles = useMemo(() => ({
    position: 'relative' as const,
    width: timelineContentLeft + timelineContentWidth + 32, // Total width including labels and padding
    height: '100%'
  }), [timelineContentLeft, timelineContentWidth])

  // Calculate initial scroll position to show the viewport range
  const initialScrollLeft = useMemo(() => {
    if (!initialViewport) return 0

    // Calculate the position of the viewport start within the full timeline
    const totalTimelineMs = dateRange.end.getTime() - dateRange.start.getTime()
    const viewportStartMs = initialViewport.start.getTime() - dateRange.start.getTime()

    // Convert to pixel position
    const scrollPosition = (viewportStartMs / totalTimelineMs) * timelineContentWidth

    return Math.max(0, scrollPosition)
  }, [initialViewport, dateRange, timelineContentWidth])

  // Set initial scroll position to show the viewport range
  useEffect(() => {
    if (containerRef.current && initialScrollLeft > 0) {
      // Use setTimeout to ensure the component is fully rendered
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollLeft = initialScrollLeft
        }
      }, 100)
    }
  }, [initialScrollLeft])

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  const handleSwimlaneHover = (category: ReleaseCategory, isHovered: boolean) => {
    setActiveSwimlane(isHovered ? category : null)
    onSwimlaneHover?.(category, isHovered)
  }
  
  const handleReleaseClick = (release: CategorizedRelease) => {
    onReleaseClick?.(release)
  }
  
  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  
  const renderLoadingState = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'text.secondary'
      }}
    >
      <Typography variant="body2">Loading release timeline...</Typography>
    </Box>
  )
  
  const renderErrorState = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'error.main'
      }}
    >
      <Typography variant="body2">
        Error loading releases: {error}
      </Typography>
    </Box>
  )
  
  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'text.secondary'
      }}
    >
      <Typography variant="body2">
        No releases found in the selected date range
      </Typography>
    </Box>
  )
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (isLoading) {
    return (
      <Paper sx={containerStyles} ref={containerRef}>
        {renderLoadingState()}
      </Paper>
    )
  }
  
  if (error) {
    return (
      <Paper sx={containerStyles} ref={containerRef}>
        {renderErrorState()}
      </Paper>
    )
  }
  
  if (releases.length === 0) {
    return (
      <Paper sx={containerStyles} ref={containerRef}>
        {renderEmptyState()}
      </Paper>
    )
  }
  
  return (
    <Paper 
      sx={containerStyles} 
      ref={containerRef}
      role="region"
      aria-label="Release timeline with 4 swimlanes"
      aria-describedby="timeline-description"
    >
      {/* Hidden description for screen readers */}
      <Box
        id="timeline-description"
        sx={{ position: 'absolute', left: '-9999px' }}
      >
        {`Release timeline showing ${releases.length} releases across 4 categories from ${dateRange.start.toLocaleDateString()} to ${dateRange.end.toLocaleDateString()}`}
      </Box>

      {/* Scrollable Content Container */}
      <Box sx={scrollableContentStyles}>
        {/* Timeline Scale Header */}
        <TimelineScale
          dateRange={dateRange}
          timelineWidth={timelineContentWidth}
          leftOffset={timelineContentLeft}
          height={60}
        />

        {/* Swimlanes */}
        {DEFAULT_SWIMLANE_CONFIGS.map((config) => {
          const position = swimlanePositions.find(p => p.category === config.category)
          if (!position) return null

          const swimlaneReleases = groupedReleases[config.category] || []

          // Adjust position to account for timeline scale header
          const adjustedPosition = {
            ...position,
            top: position.top + 60, // Offset by timeline scale height
            left: timelineContentLeft,
            width: timelineContentWidth
          }

          return (
            <SwimLane
              key={config.category}
              category={config.category}
              releases={(() => {
                if (config.category === 'Software') {
                  const k8sInSwimlane = swimlaneReleases.filter(r => r.name.toLowerCase().includes('k8s'))
                  console.log(`DEBUG: SwimLane ${config.category} receiving k8s releases:`, k8sInSwimlane.map(r => r.name))
                }
                return swimlaneReleases
              })()}
              position={adjustedPosition}
              dateRange={dateRange}
              timelineScale={adaptivePixelsPerDay}
              showLabel={showLabels}
              isActive={activeSwimlane === config.category}
              onReleaseClick={handleReleaseClick}
              onSwimlaneHover={handleSwimlaneHover}
              enableInteractions={enableInteractions}
            />
          )
        })}
      </Box>
    </Paper>
  )
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

export default React.memo(ReleaseTimelineContainer)

// ============================================================================
// COMPONENT DISPLAY NAME
// ============================================================================

ReleaseTimelineContainer.displayName = 'ReleaseTimelineContainer'
